import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AGING_BANDS,
  type AgingBand,
  type AgingRow,
  type BandCustomers,
  type PeriodQuery,
  type ReceivablesReport,
  type TopList,
  reportsApi,
} from "../../api/reports";
import type { ChartPalette } from "../../theme/useChartPalette";
import { formatDate, formatNumber } from "../../lib/format";
import { KpiTile } from "../../components/reports/KpiTile";
import { StabloRegistar } from "../../components/reports/StabloRegistar";
import type { StabloRed } from "../../components/reports/StabloRegistar";
import { useToast } from "../../components/Toast";
import { th } from "./stil";

/**
 * Potraživanja: starost, avansi, zemlje i saldo po mesecima.
 *
 * Tri stvari ekran mora da kaže, inače pokazuje tačne brojeve o kojima čitalac ne zna šta drži:
 *
 * 1. **Koji je dan.** ERP-ov snimak ne nosi datum i ERP ga prepisuje, pa je dan koji se prikazuje
 *    dan uvoza, i retko je zadnji dan perioda.
 * 2. **Ukupno je činjenica, podela na kofe je procena.** Veze uplate i računa u ERP-u nema nigde,
 *    pa raspodelu salda po kofama pravi ERP-ova procedura.
 * 3. **Kofe nisu one sa ekrana finansija.** Tamo je 7/30/60/120, ovde 7/30/60/90 plus pet
 *    nedospelih — to je odluka, a ne nesaglasnost, i piše na ekranu.
 */

/** Kofa je dospela od `OVERDUE_7` nadalje; isti prelom kao u Javi, i samo na jednom mestu. */
const PRVA_DOSPELA = AGING_BANDS.indexOf("OVERDUE_7");
const jeDospela = (b: AgingBand) => AGING_BANDS.indexOf(b) >= PRVA_DOSPELA;

export default function PotrazivanjaTab({
  podaci,
  upit,
  locale,
  palette,
  ucitava,
}: {
  podaci: ReceivablesReport;
  upit: PeriodQuery;
  locale: string;
  palette: ChartPalette;
  ucitava: boolean;
}) {
  const { t } = useTranslation();

  const imaSnimak = podaci.snapshot.actual != null;

  /** Valuta sa najvećim ukupnim iznosom — ona koju grafikon crta. */
  const glavna = useMemo<AgingRow | null>(() => {
    const sve = [...podaci.domestic, ...podaci.foreign];
    return sve.length === 0 ? null : sve.reduce((a, b) => (b.total > a.total ? b : a));
  }, [podaci]);

  const kofe = useMemo(() => {
    if (!glavna) return [];
    return AGING_BANDS.map((b) => ({
      kofa: b,
      ime: t(`report.recv.band.${b}`, b),
      iznos: glavna.bands[b] ?? 0,
      dospela: jeDospela(b),
    }));
  }, [glavna, t]);

  /**
   * Zbirovi idu PO VALUTI — dinari se ne sabiraju sa evrima.
   *
   * Prva verzija je sve redove sabirala u jedan broj, pa je pločica „Ukupno potraživanje" pisala
   * 355.375.862,56: to je 352.499.602,94 u dinarima plus 2.876.259,62 u evrima, sabrano kao da su
   * ista veličina. Domaći redovi idu prvi, pa je dinar prvi u mapi; ostale valute se imenuju.
   */
  // Dve decimale, kao svuda u izveštaju. Hoistovano: čuvar ne vidi vitičastu zagradu u t(...).
  const DVE = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

  const zbirovi = useMemo(() => {
    const sve = [...podaci.domestic, ...podaci.foreign];
    const po = (f: (r: (typeof sve)[number]) => number) => {
      const m = new Map<string, number>();
      for (const r of sve) m.set(r.currency, (m.get(r.currency) ?? 0) + f(r));
      return m;
    };
    const ukupnoPo = po((r) => r.total);
    const valuta = ukupnoPo.has("RSD") ? "RSD" : ([...ukupnoPo.keys()][0] ?? "RSD");
    const uzmi = (m: Map<string, number>) => m.get(valuta) ?? 0;
    const ostale = (m: Map<string, number>) =>
      [...m.entries()].filter(([v, x]) => v !== valuta && x !== 0);
    const sporno = podaci.disputed.reduce((s, r) => s + r.amount, 0);
    return {
      valuta,
      ukupno: uzmi(ukupnoPo),
      ukupnoOstale: ostale(ukupnoPo),
      dospelo: uzmi(po((r) => r.overdue)),
      dospeloOstale: ostale(po((r) => r.overdue)),
      preko90: uzmi(po((r) => r.bands.OVERDUE_90_PLUS ?? 0)),
      preko90Ostale: ostale(po((r) => r.bands.OVERDUE_90_PLUS ?? 0)),
      sporno,
    };
  }, [podaci]);

  /** „uz to 2.876.259,62 EUR" — ostale valute se imenuju, ne pribrajaju. */
  const uzValute = (ostale: [string, number][]) =>
    ostale.length === 0
      ? undefined
      : t("report.recv.alsoInCurrency", {
          iznos: ostale
            .map(([v, x]) => `${formatNumber(x, locale, DVE)} ${v}`)
            .join(", "),
          defaultValue: "uz to {{iznos}}",
        });

  const niz = useMemo(
    () =>
      podaci.balanceSeries.map((b) => ({
        ime: `${String(b.month).padStart(2, "0")}/${String(b.year).slice(2)}`,
        saldo: b.closingBalance,
      })),
    [podaci.balanceSeries],
  );

  const zemlje = useMemo(() => podaci.countries.slice(0, 12), [podaci.countries]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, opacity: ucitava ? 0.55 : 1 }}>
      {/* Koji je dan — prvo, jer menja značenje svega pod njim. */}
      <div
        className="card"
        style={{
          padding: "10px 14px",
          fontSize: 13,
          color: "var(--ink-2)",
          borderLeft: `3px solid ${imaSnimak && podaci.snapshot.exact ? "var(--ok)" : "var(--warn)"}`,
        }}
      >
        {!imaSnimak
          ? t("report.recv.noSnapshot", {
              najstariji: podaci.snapshot.earliestHeld
                ? formatDate(podaci.snapshot.earliestHeld, locale)
                : "—",
              defaultValue:
                "Za ovaj period nema snimka potraživanja. Najstariji koji portal drži je {{najstariji}}; ERP svoj snimak prepisuje, pa stariji ne postoji ni kod njega.",
            })
          : podaci.snapshot.exact
            ? t("report.recv.snapshotExact", {
                dan: formatDate(podaci.snapshot.actual, locale),
                defaultValue: "Stanje na dan {{dan}}, poslednji dan perioda.",
              })
            : t("report.recv.snapshotApprox", {
                dan: formatDate(podaci.snapshot.actual, locale),
                dana: podaci.snapshot.daysOff,
                defaultValue:
                  "Stanje na dan {{dan}} — {{dana}} dana pre kraja perioda. ERP svoj snimak prepisuje pri svakom računanju, pa je ovo jedini koji postoji.",
              })}
        {/* Tri datuma, i svaki se imenuje: ko ih spoji dobije stanje koje izgleda svežije nego
            što jeste. Dan obračuna dolazi iz ERP-ovog radnog zapisa, presek putuje sa uvozom. */}
        {imaSnimak && podaci.snapshot.sourceCutoff && podaci.snapshot.syncedAt && (
          <div className="sub" style={{ marginTop: 4 }}>
            {t("report.recv.snapshotDates", {
              presek: formatDate(podaci.snapshot.sourceCutoff, locale),
              uvoz: formatDate(podaci.snapshot.syncedAt, locale),
              defaultValue: "Dan obračuna je ERP-ov, iz njegovog radnog zapisa. Kopija baze seže do {{presek}}; u portal je uvezeno {{uvoz}}.",
            })}
          </div>
        )}
      </div>

      {/* Potraživanja nemaju poređenje sa prošlom godinom — snimak je jedan trenutak, ne period.
          Zato pločice nose samo vrednost; `previous` izostaje i promena se ne crta.

          BEZ SNIMKA SE NE ISPISUJE NULA. Četiri nule ne kažu „nema snimka" nego „niko ništa ne
          duguje" — a to je tvrdnja koju ekran nema odakle da zna. Isto važi i za „Sporno": kad
          nijedan iznos nije označen, to nije nula nego NIJE OZNAČENO. */}
      {imaSnimak && (
        <div className="kpi-grid" style={{ marginBottom: 0 }}>
          <KpiTile
            label={t("report.recv.kpi.total", "Ukupno potraživanje")}
            value={zbirovi.ukupno}
            unit={zbirovi.valuta}
            locale={locale}
            moreIsBetter={false}
            note={uzValute(zbirovi.ukupnoOstale)}
          />
          <KpiTile
            label={t("report.recv.kpi.overdue", "Dospelo")}
            value={zbirovi.dospelo}
            unit={zbirovi.valuta}
            locale={locale}
            moreIsBetter={false}
            note={uzValute(zbirovi.dospeloOstale)}
          />
          <KpiTile
            label={t("report.recv.kpi.over90", "Preko 90 dana")}
            value={zbirovi.preko90}
            unit={zbirovi.valuta}
            locale={locale}
            moreIsBetter={false}
            note={uzValute(zbirovi.preko90Ostale)}
          />
          {podaci.disputed.length === 0 ? (
            <div className="kpi">
              <div className="label">{t("report.recv.kpi.disputed", "Sporno")}</div>
              <div className="val" style={{ color: "var(--ink-3)" }}>
                {t("report.recv.notMarked", "nije označeno")}
              </div>
              <div className="sub" style={{ fontSize: 12, color: "var(--ink-3)" }}>
                {t("report.recv.kpi.disputedSub", "naša oznaka, ne ERP-ova")}
              </div>
            </div>
          ) : (
            <KpiTile
              label={t("report.recv.kpi.disputed", "Sporno")}
              value={zbirovi.sporno}
              locale={locale}
              moreIsBetter={false}
              note={t("report.recv.kpi.disputedSub", "naša oznaka, ne ERP-ova")}
            />
          )}
        </div>
      )}

      {glavna && (
        <section className="card" style={{ padding: 14 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
            {t("report.recv.chart.aging", { valuta: glavna.currency, defaultValue: "Starost u {{valuta}}" })}
          </h3>
          <div className="sub" style={{ marginBottom: 10 }}>
            {t(
              "report.recv.chart.agingSub",
              "Ukupno po kupcu je saldo iz knjige; podela na kofe je raspodela ERP-a, jer veze uplate i računa u ERP-u nema.",
            )}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={kofe} margin={{ top: 4, right: 8, left: 8, bottom: 56 }}>
              <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="ime"
                interval={0}
                angle={-32}
                textAnchor="end"
                height={64}
                tick={{ fontSize: 10, fill: palette.ink }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: palette.ink }}
                axisLine={false}
                tickLine={false}
                width={78}
                tickFormatter={(v: number) => formatNumber(v, locale, { maximumFractionDigits: 0 })}
              />
              <Tooltip
                contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
                labelStyle={{ color: palette.inkStrong }}
                formatter={(v) => formatNumber(Number(v), locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              />
              <Bar dataKey="iznos" name={t("report.recv.col.amount", "Iznos")} radius={[3, 3, 0, 0]}>
                {kofe.map((k) => (
                  /* Dospelo se boji upozorenjem: na slici mora da se vidi gde je granica, jer je
                     to jedina razlika koja menja šta se s tim radi. */
                  <Cell key={k.kofa} fill={k.dospela ? "var(--warn)" : palette.inkMuted} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {niz.length > 1 && (
        <section className="card" style={{ padding: 14 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
            {t("report.recv.chart.balance", "Saldo kupaca po mesecima")}
          </h3>
          <div className="sub" style={{ marginBottom: 10 }}>
            {t(
              "report.recv.chart.balanceSub",
              "Iz proknjižene glavne knjige, kumulativ unutar godine — jedini deo ovog odeljka koji ima istoriju.",
            )}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={niz} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
              <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="ime" tick={{ fontSize: 10, fill: palette.ink }} />
              <YAxis
                tick={{ fontSize: 11, fill: palette.ink }}
                axisLine={false}
                tickLine={false}
                width={78}
                tickFormatter={(v: number) => formatNumber(v, locale, { maximumFractionDigits: 0 })}
              />
              <Tooltip
                contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
                labelStyle={{ color: palette.inkStrong }}
                formatter={(v) => formatNumber(Number(v), locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              />
              <Area
                type="monotone"
                dataKey="saldo"
                name={t("report.recv.col.closing", "Konačni saldo")}
                stroke={palette.brand}
                fill={palette.fill}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </section>
      )}

      <StarostTabela
        naslov={t("report.recv.domestic", "Kupci u zemlji")}
        redovi={podaci.domestic}
        opseg="DOMESTIC"
        upit={upit}
        locale={locale}
      />
      <StarostTabela
        naslov={t("report.recv.foreign", "Kupci u inostranstvu")}
        redovi={podaci.foreign}
        opseg="FOREIGN"
        upit={upit}
        locale={locale}
      />

      {/* Jedna tabela po valuti. Rang preko valuta poredi brojeve a ne novac, pa je inostrani
          kupac nestajao sa spiska; original razdvaja isto tako. */}
      {podaci.topDebtors.map((v) => (
        <KupciTabela
          key={`domaci-${v.currency}`}
          naslov={`${t("report.recv.topDebtors", "Najveći dužnici")} — ${v.currency}`}
          lista={v}
          locale={locale}
        />
      ))}

      {/* Spisak inostranih dužnika original traži, API ga je oduvek nosio, a ekran ga nije crtao. */}
      {podaci.topForeignDebtors.map((v) => (
        <KupciTabela
          key={`ino-${v.currency}`}
          naslov={`${t("report.recv.topForeignDebtors", "Najveći inostrani dužnici")} — ${v.currency}`}
          lista={v}
          locale={locale}
        />
      ))}

      {zemlje.length > 0 && (
        <section className="card" style={{ padding: 0, overflowX: "auto" }}>
          <h3 style={{ margin: 0, padding: "14px 14px 0", fontSize: 15 }}>
            {t("report.recv.countries", "Inostrana izloženost po zemljama")}
          </h3>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("report.recv.col.country", "Zemlja")}</th>
                <th>{t("report.recv.col.currency", "Valuta")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.balance", "Saldo")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.overdue", "Dospelo")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.customers", "Kupaca")}</th>
              </tr>
            </thead>
            <tbody>
              {zemlje.map((z) => (
                <tr key={`${z.country}-${z.currency}`}>
                  <td>{z.country || t("report.recv.noCountry", "bez zemlje")}</td>
                  <td>{z.currency}</td>
                  <td className="num">{formatNumber(z.balance, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="num">{formatNumber(z.overdue, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="num">{z.customers}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {podaci.countries.length > zemlje.length && (
            <div className="sub" style={{ padding: "8px 14px 14px" }}>
              {t("report.recv.cutCountries", {
                prikazano: zemlje.length,
                ukupno: podaci.countries.length,
                defaultValue: "Prikazano {{prikazano}} od {{ukupno}} zemalja; ostale su u Excel izvozu.",
              })}
            </div>
          )}
        </section>
      )}

      <AvansiTabela
        primljeni={podaci.advancesReceived}
        dati={podaci.advancesGiven}
        locale={locale}
      />
    </div>
  );
}


/** Starost po valuti: sve deset kofa, i nule se pišu. */
/**
 * Starost potraživanja, sa spuštanjem do kupca.
 *
 * <p>Zatečena tabela je bila krstasta: red je valuta, a trinaest kolona su nedospelo, dospelo,
 * ukupno i deset kofa. Trinaest brojeva u redu se čita samo prstom po ekranu, a na pitanje koje
 * tabela otvara — KO je u kofi od 90 dana — nije imala odgovor.
 *
 * <p>Ovde je po valuti jedan registar u tri nivoa: nedospelo i dospelo, pod njima kofe, pod kofom
 * kupci sa servera. Nijedan broj nije sklonjen: ukupno stoji u zbiru, broj kupaca pored njega.
 *
 * <p>Dve ograde putuju sa prikazom. **Ukupno je saldo iz knjige, a kofe su ERP-ova procena** — i
 * ako se zbir kofa i saldo raziđu, razlika se IMENUJE, ne prećuti: saldo se uzima `max`-om po
 * kupcu jer ga ERP ponavlja na svakoj kofi, pa poklapanje nije zagarantovano konstrukcijom.
 * **Spisak u kofi je odsečen na deset** i kaže koliko ih je i koliko nosi ostatak.
 */
function StarostTabela({
  naslov,
  redovi,
  opseg,
  upit,
  locale,
}: {
  naslov: string;
  redovi: AgingRow[];
  opseg: "DOMESTIC" | "FOREIGN";
  upit: PeriodQuery;
  locale: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  // Grupe (nedospelo/dospelo) su OTVORENE zatečeno — svih deset kofa se vidi odjednom, kao i u
  // zatečenoj krstastoj tabeli; sklapaju se namerno. Kofe su zatvorene, jer bi inače svaka
  // tražila svoje kupce sa servera.
  const [sklopljene, setSklopljene] = useState<Set<string>>(new Set());
  const [otvoreneKofe, setOtvoreneKofe] = useState<Set<string>>(new Set());
  const [kupci, setKupci] = useState<Record<string, BandCustomers>>({});
  const [dovlaci, setDovlaci] = useState<string | null>(null);

  const kljuceviGrupa = useMemo(
    () => redovi.flatMap((r) => [`${r.currency}\u0000NEDOSPELO`, `${r.currency}\u0000DOSPELO`]),
    [redovi],
  );
  const otvoreni = useMemo(() => {
    const sve = new Set(otvoreneKofe);
    for (const k of kljuceviGrupa) if (!sklopljene.has(k)) sve.add(k);
    return sve;
  }, [kljuceviGrupa, otvoreneKofe, sklopljene]);

  const otvori = useCallback(
    async (kljuc: string) => {
      // Ključ je putanja: „RSD\u0000DOSPELO" je grupa, „RSD\u0000OVERDUE_90_PLUS" je kofa.
      const deo = kljuc.split("\u0000");
      const jeKofa = deo.length === 2 && AGING_BANDS.includes(deo[1] as AgingBand);
      if (!jeKofa) {
        setSklopljene((pre) => {
          const sled = new Set(pre);
          if (sled.has(kljuc)) sled.delete(kljuc);
          else sled.add(kljuc);
          return sled;
        });
        return;
      }

      const bio = otvoreneKofe.has(kljuc);
      setOtvoreneKofe((pre) => {
        const sled = new Set(pre);
        if (bio) sled.delete(kljuc);
        else sled.add(kljuc);
        return sled;
      });
      if (bio || kupci[kljuc]) return;

      setDovlaci(kljuc);
      try {
        const o = await reportsApi.receivableBandCustomers(upit, opseg, deo[0], deo[1]);
        // Spor odgovor ne sme da sleti pod kofu koju je čovek u međuvremenu otvorio.
        if (o.currency === deo[0] && o.band === deo[1] && o.scope === opseg) {
          setKupci((pre) => ({ ...pre, [kljuc]: o }));
        }
      } catch (e) {
        toast.error((e as Error).message);
        setOtvoreneKofe((pre) => {
          const sled = new Set(pre);
          sled.delete(kljuc);
          return sled;
        });
      } finally {
        setDovlaci((k) => (k === kljuc ? null : k));
      }
    },
    [kupci, opseg, otvoreneKofe, toast, upit],
  );

  if (redovi.length === 0) return null;

  return (
    <section className="card" style={{ padding: 0 }}>
      <h3 style={{ margin: 0, padding: "14px 14px 0", fontSize: 15 }}>{naslov}</h3>
      {redovi.map((r) => (
        <Valuta
          key={r.currency}
          red={r}
          opseg={opseg}
          kupci={kupci}
          dovlaci={dovlaci}
          otvoreni={otvoreni}
          naOtvaranje={otvori}
          locale={locale}
          t={t}
        />
      ))}
    </section>
  );
}

/** Jedna valuta: svoj registar, jer udeo u ukupnom nema smisla preko dve valute. */
function Valuta({
  red,
  kupci,
  dovlaci,
  otvoreni,
  naOtvaranje,
  locale,
  t,
}: {
  red: AgingRow;
  opseg: "DOMESTIC" | "FOREIGN";
  kupci: Record<string, BandCustomers>;
  dovlaci: string | null;
  otvoreni: Set<string>;
  naOtvaranje: (kljuc: string) => void;
  locale: string;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const grupa = (dospele: boolean): StabloRed => {
    const kofe = AGING_BANDS.filter((b) => jeDospela(b) === dospele);
    return {
      kljuc: `${red.currency}\u0000${dospele ? "DOSPELO" : "NEDOSPELO"}`,
      ime: dospele ? t("report.recv.col.overdue", "Dospelo") : t("report.recv.col.notDue", "Nedospelo"),
      udeo: red.total === 0 ? null : ((dospele ? red.overdue : red.notDue) / red.total) * 100,
      mere: [{ sada: dospele ? red.overdue : red.notDue, decimala: 2, viseJeBolje: false, nulaSePise: true }],
      mozeDaSeOtvori: true,
      deca: kofe.map((b) => {
        const kljuc = `${red.currency}\u0000${b}`;
        const dovuceni = kupci[kljuc];
        return {
          kljuc,
          ime: t(`report.recv.band.${b}`, b),
          udeo: red.total === 0 ? null : ((red.bands[b] ?? 0) / red.total) * 100,
          mere: [{ sada: red.bands[b] ?? 0, decimala: 2, viseJeBolje: false, nulaSePise: true }],
          mozeDaSeOtvori: (red.bands[b] ?? 0) !== 0,
          prazno: t("report.recv.bandEmpty", "U ovoj grupi dospelosti nema nijednog kupca."),
          // Odsečen spisak KAŽE da je odsečen, i koliko nosi ostatak.
          dodatak:
            dovuceni && dovuceni.total > dovuceni.shown
              ? t("report.recv.bandCut", {
                  prikazano: dovuceni.shown,
                  ukupno: dovuceni.total,
                  ostatak: formatNumber(dovuceni.restAmount, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                  defaultValue: "prikazano {{prikazano}} od {{ukupno}}; ostatak nosi {{ostatak}}",
                })
              : undefined,
          deca: dovuceni?.rows.map((k) => ({
            kljuc: `${kljuc}\u0000${k.erpCustomerId}`,
            ime: k.name || k.erpCustomerId,
            oznaka:
              k.oldestDays == null
                ? undefined
                : t("report.recv.oldestDays", { broj: k.oldestDays, defaultValue: "{{broj}} dana" }),
            oznakaTon: "tiho" as const,
            udeo: red.total === 0 ? null : (k.amount / red.total) * 100,
            mere: [{ sada: k.amount, decimala: 2, viseJeBolje: false }],
            // Saldo kupca je druga veličina od njegovog iznosa u kofi, pa stoji imenovan pored.
            dodatak: t("report.recv.balanceBeside", {
              iznos: formatNumber(k.balance, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              defaultValue: "saldo {{iznos}}",
            }),
          })),
        };
      }),
    };
  };

  // Ako se zbir kofa i saldo raziđu, razlika se imenuje. Saldo se uzima `max`-om po kupcu jer ga
  // ERP ponavlja na svakoj kofi, pa poklapanje nije zagarantovano konstrukcijom.
  const razlika = red.total - (red.notDue + red.overdue);
  const raziloSe = Math.abs(razlika) > 0.01;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ ...th, padding: "0 14px 6px" }}>{red.currency}</div>
      <StabloRegistar
        kolone={[t("report.recv.col.band", "Starost"), t("report.recv.col.amount", "Iznos")]}
        naslovUdela={t("report.recv.col.shareOfTotal", "Udeo u saldu")}
        redovi={[grupa(false), grupa(true)]}
        otvoreni={otvoreni}
        naOtvaranje={naOtvaranje}
        ucitava={dovlaci}
        zbir={{
          kljuc: `__ukupno-${red.currency}`,
          ime: t("report.recv.col.total", "Ukupno"),
          udeo: red.total === 0 ? null : 100,
          mere: [{ sada: red.total, decimala: 2, viseJeBolje: false }],
          dodatak: t("report.recv.customerCount", { count: red.customers, defaultValue: "kupaca: {{count}}" }),
        }}
        locale={locale}
      />
      {raziloSe && (
        <div className="sub" style={{ padding: "8px 14px 0", borderLeft: "3px solid var(--warn)" }}>
          {t("report.recv.bandsVsBalance", {
            iznos: formatNumber(razlika, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            defaultValue:
              "Zbir po dospelosti i saldo se razilaze za {{iznos}}. Saldo je iz knjige i činjenica je; podela po dospelosti je ERP-ova procena.",
          })}
        </div>
      )}
    </div>
  );
}

function KupciTabela({ naslov, lista, locale }: { naslov: string; lista: TopList; locale: string }) {
  const { t } = useTranslation();
  return (
    <section className="card" style={{ padding: 0, overflowX: "auto" }}>
      <h3 style={{ margin: 0, padding: "14px 14px 0", fontSize: 15 }}>{naslov}</h3>
      {lista.rows.length === 0 ? (
        <div className="empty" style={{ padding: 14 }}>
          {t("report.recv.noCustomers", "Za ovaj snimak nema pozicija kupaca.")}
        </div>
      ) : (
        <>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("report.recv.col.customer", "Kupac")}</th>
                <th>{t("report.recv.col.group", "Grupa")}</th>
                <th>{t("report.recv.col.salesperson", "Komercijalista")}</th>
                <th>{t("report.recv.col.currency", "Valuta")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.balance", "Saldo")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.overdue", "Dospelo")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.over90", "Preko 90")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.disputed", "Sporno")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.oldestDays", "Najstarije, dana")}</th>
              </tr>
            </thead>
            <tbody>
              {lista.rows.map((p) => (
                <tr key={`${p.erpCustomerId}-${p.currency}`}>
                  <td>{p.name}</td>
                  <td>{p.customerGroup}</td>
                  <td>
                    {p.salesperson || t("report.recv.noSalesperson", "bez komercijaliste")}
                  </td>
                  <td>{p.currency}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{formatNumber(p.balance, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="num">{formatNumber(p.overdue, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="num" style={{ color: p.overdue90Plus > 0 ? "var(--warn)" : "var(--ink-3)" }}>
                    {formatNumber(p.overdue90Plus, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="num">{p.disputed > 0 ? formatNumber(p.disputed, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}</td>
                  <td className="num">{p.oldestDays ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {lista.total > lista.shown && (
            /* Odsečen spisak mora da kaže da je odsečen, i koliko je ostatak — inače se sabere
               kolona i dobije broj manji od ukupnog, bez ikakvog znaka da nešto nije tu. */
            <div className="sub" style={{ padding: "8px 14px 14px" }}>
              {t("report.recv.cut", {
                prikazano: lista.shown,
                ukupno: lista.total,
                ostatak: formatNumber(lista.restAmount, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                defaultValue: "Prikazano {{prikazano}} od {{ukupno}}; ostatak je {{ostatak}}.",
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function AvansiTabela({
  primljeni,
  dati,
  locale,
}: {
  primljeni: ReceivablesReport["advancesReceived"];
  dati: ReceivablesReport["advancesGiven"];
  locale: string;
}) {
  const { t } = useTranslation();
  const redovi = [
    ...primljeni.map((a) => ({ ...a, vrsta: t("report.recv.advancesReceived", "Primljeni avansi") })),
    ...dati.map((a) => ({ ...a, vrsta: t("report.recv.advancesGiven", "Dati avansi") })),
  ];
  if (redovi.length === 0) return null;
  const imaZaostalih = redovi.some((r) => r.retiredAccount != null);
  return (
    <section className="card" style={{ padding: 0, overflowX: "auto" }}>
      <h3 style={{ margin: 0, padding: "14px 14px 0", fontSize: 15 }}>{t("report.recv.advances", "Avansi")}</h3>
      {imaZaostalih && (
        /* Zašto ugašeni broj konta ima svoju kolonu: štampani izveštaj čita stari broj i zato
           prikazuje 77.290 tamo gde živi konto nosi 205 miliona. */
        <div className="sub" style={{ padding: "6px 14px 0" }}>
          {t(
            "report.recv.retiredNote",
            "Konta 4302 i 4306 su 01.01.2021. prenumerisana u 4300 i 43020. Iznos je sa živog konta; ono što je ostalo na starom broju stoji uz njega.",
          )}
        </div>
      )}
      <table className="tbl">
        <thead>
          <tr>
            <th>{t("report.recv.col.kind", "Vrsta")}</th>
            <th>{t("report.recv.col.currency", "Valuta")}</th>
            <th style={{ textAlign: "right" }}>{t("report.recv.col.amount", "Iznos")}</th>
            <th style={{ textAlign: "right" }}>{t("report.recv.col.customers", "Kupaca")}</th>
            <th>{t("report.recv.col.retiredAccount", "Ugašeni konto")}</th>
            <th style={{ textAlign: "right" }}>
              {t("report.recv.col.retiredAmount", "Ostalo na ugašenom")}
            </th>
          </tr>
        </thead>
        <tbody>
          {redovi.map((a) => (
            <tr key={`${a.vrsta}-${a.currency}`}>
              <td>{a.vrsta}</td>
              <td>{a.currency}</td>
              <td className="num" style={{ fontWeight: 600 }}>{formatNumber(a.amount, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td className="num">{a.customers}</td>
              <td>{a.retiredAccount ?? "—"}</td>
              <td className="num" style={{ color: a.retiredAmount > 0 ? "var(--warn)" : "var(--ink-3)" }}>
                {a.retiredAmount > 0 ? formatNumber(a.retiredAmount, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
